/**
 * The headline at the top of the dashboard: sales for the period as the one
 * big number, with the change vs. last year and how many accounts were
 * active on the line below, then the MTD | YTD picker. Follows the
 * All | Assigned | Unassigned picker and the picked period.
 */

import { useTranslation } from "react-i18next";
import type { BookScope, SalesInsightsBookResponse } from "@onsemble/shared/types/sales-insights";
import { count, dollars, growth } from "../../format";
import { CHANGE_TONE, changeTone } from "../../shared/changeTone";
import { PeriodTabs, type Period } from "../../shared/PeriodTabs";

export function Summary({
  book,
  scope,
  period,
  onPeriodChange,
}: {
  book: SalesInsightsBookResponse;
  /** The picked All | Assigned | Unassigned choice */
  scope: BookScope;
  /** Month to date or year to date */
  period: Period;
  onPeriodChange: (next: Period) => void;
}) {
  const { t } = useTranslation();
  // The picked choice's totals; members only have Assigned
  const totals = book.book[scope] ?? book.book.assigned;
  // Then the period's numbers: this month so far, or this year so far
  const year = period === "year";
  const sales = year ? totals.salesYtd : totals.salesMtd;
  const active = year ? totals.activeThisYear : totals.activeThisMonth;
  const tone = CHANGE_TONE[changeTone(sales.growth)];

  return (
    <div>
      {/* Label */}
      <div className="text-si-sm font-medium text-si-ink-2">
        {t(year ? "pages.pcInsights.summary.salesYtd" : "pages.pcInsights.summary.salesMtd")}
      </div>
      {/* The one big number: sales for the period */}
      <div className="mt-1 text-si-3xl font-medium leading-[1.05] tracking-[-0.035em] tabular-nums">
        {dollars(sales.sales)}
      </div>
      {/* Change vs. the same days last year, then how many accounts had sales */}
      <div className="mt-1.5 text-si-md tabular-nums">
        <span className={tone.className}>
          {tone.glyph}
          {growth(sales.growth)}
        </span>
        <span className="text-si-ink-2">
          {" · "}
          {t(
            year ? "pages.pcInsights.summary.activeLineYear" : "pages.pcInsights.summary.activeLineMonth",
            { active: count(active.active), total: count(active.total) },
          )}
        </span>
      </div>
      {/* MTD | YTD picker with the black bar centered under it, as a divider */}
      <div className="inline-flex flex-col items-center">
        <PeriodTabs value={period} onChange={onPeriodChange} />
        {/* Short black bar centered under the picker */}
        <div aria-hidden="true" className="mt-3 h-[3px] w-full rounded-full bg-si-ink" />
      </div>
    </div>
  );
}
